import {
  ConversationFeedbackDialog,
  type FeedbackDialogRef,
} from '@github-ui/copilot-chat/components/ConversationFeedbackDialog'
import {PersonalInstructionsDialog} from '@github-ui/copilot-chat/components/PersonalInstructionsDialog'
import {StaffDialogs} from '@github-ui/copilot-chat/components/StaffDialogs'
import {
  hasCustomInstructions,
  hasCustomInstructionsAsync,
  makeRepositoryReference,
} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {type CopilotChatThread, DialogType} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {getSelectedThread} from '@github-ui/copilot-chat/utils/get-selected-thread'
import {StaffToolsMenu} from '@github-ui/copilot-immersive-v1/components/StaffToolsMenu'
import {sendEvent} from '@github-ui/hydro-analytics'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {isStaff} from '@github-ui/stats'
import {CommandIconButton} from '@github-ui/ui-commands'
import {
  ArrowLeftIcon,
  ArrowUpRightIcon,
  BeakerIcon,
  CommentDiscussionIcon,
  ComposeIcon,
  GrabberIcon,
  HistoryIcon,
  KebabHorizontalIcon,
  NoteIcon,
  ToolsIcon,
  TrashIcon,
  XIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './Header.module.css'

export interface HeaderProps {
  staffDialogRef: React.RefObject<HTMLDivElement | null>
  showStaffDialog: DialogType
  setShowStaffDialog: (value: DialogType) => void
  isImmersive?: boolean
  isResponding?: boolean
  onDragStart?: (e: React.MouseEvent) => void
  onDragKeyDown?: (e: React.KeyboardEvent) => void
  isDragging?: boolean
  hideCloseButton?: boolean
  forceVisible?: boolean
}

export const Header = (props: HeaderProps) => {
  const state = useChatState()
  const manager = useChatManager()
  const [showPersonalInstructionsDialog, setShowPersonalInstructionsDialog] = useState(false)

  const thread = getSelectedThread(state)
  const {messages, streamingMessage} = state
  const isVisible = state.chatIsOpen || props.forceVisible

  const handleThreadDelete = useCallback(() => {
    if (thread) void manager.deleteThread(thread)
  }, [thread, manager])

  const handleBackToThread = useCallback(() => {
    void manager.selectThread(thread ?? null)
  }, [manager, thread])

  const menuAnchorRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={clsx(styles.HeaderContainer, props.isDragging && styles.dragging)}>
      {props.onDragStart && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={styles.dragHandle} onMouseDown={props.onDragStart}>
          <button
            type="button"
            className={styles.grabberButton}
            onKeyDown={props.onDragKeyDown}
            aria-label="Move chat panel"
            aria-roledescription="draggable"
            aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
          >
            <GrabberIcon className={styles.grabberIcon} />
          </button>
        </div>
      )}
      {isVisible && state.currentView === 'list' && (
        <div className={styles.backButtonContainer}>
          <Button variant="invisible" onClick={handleBackToThread} className={styles.backButton}>
            <ArrowLeftIcon className="mr-2" />
            Back
          </Button>
        </div>
      )}
      <div className={styles.headerActions}>
        {isVisible && (
          <>
            <StaffToolsMenu inline />
            {state.renderBetaLabel && <BetaLabel className="mr-2" />}
            {state.currentView === 'thread' ? (
              <>
                <IconButton
                  hidden={messages.length === 0}
                  variant="invisible"
                  icon={ComposeIcon}
                  aria-label="New conversation"
                  tooltipDirection="s"
                  onClick={() => {
                    void manager.selectThread(null)
                  }}
                  className={styles.actionButton}
                />
                <ThreadOptionButton
                  anchorRef={menuAnchorRef}
                  handleDelete={handleThreadDelete}
                  thread={thread}
                  setShowStaffDialog={props.setShowStaffDialog}
                  setShowPersonalInstructionsDialog={setShowPersonalInstructionsDialog}
                />
              </>
            ) : (
              <IconButton
                variant="invisible"
                icon={ComposeIcon}
                aria-label="New conversation"
                tooltipDirection="w"
                onClick={() => {
                  void manager.selectThread(null)
                }}
                className={styles.actionButton}
              />
            )}
          </>
        )}

        {!props.isImmersive && (
          <>
            {isVisible && <div className={styles.buttonSeparator} />}
            <CommandIconButton
              commandId="copilot-chat:continue-in-immersive"
              variant="invisible"
              icon={ArrowUpRightIcon}
              aria-label="Continue in immersive"
              disabled={!!streamingMessage}
              tooltipDirection="sw"
              className={styles.actionButton}
            />
            {!props.hideCloseButton && (
              <CommandIconButton
                commandId="copilot-chat:close-assistive"
                variant="invisible"
                icon={XIcon}
                tooltipDirection="sw"
                className={styles.actionButton}
              />
            )}
          </>
        )}
      </div>
      <StaffDialogs
        dialogType={props.showStaffDialog}
        staffDialogRef={props.staffDialogRef}
        onDismiss={() => props.setShowStaffDialog(DialogType.None)}
      />
      {showPersonalInstructionsDialog && (
        <PersonalInstructionsDialog
          returnFocusRef={menuAnchorRef}
          onDismiss={() => setShowPersonalInstructionsDialog(false)}
        />
      )}
    </div>
  )
}

interface ThreadOptionButtonProps {
  handleDelete: () => void
  thread?: CopilotChatThread | null
  setShowStaffDialog: (value: DialogType) => void
  setShowPersonalInstructionsDialog: (value: boolean) => void
  anchorRef: RefObject<HTMLButtonElement | null>
}

export const ThreadOptionButton = (props: ThreadOptionButtonProps) => {
  const [open, setOpen] = useState(false)
  const {repoCustomInstructionsEnabled, currentReferences, optedInToUserFeedback} = useChatState()

  const manager = useChatManager()
  const feedbackRef = useRef<FeedbackDialogRef>(null)
  const showPromptDialog = copilotFeatureFlags.staffPromptDialog

  const [customInstructionsExist, setCustomInstructionsExist] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchHasCustomInstructions = async () => {
      if (copilotFeatureFlags.customInstructionsFileReferences) {
        const fetchRepoForInstructions = async (repoID: number) => {
          const res = await manager.service.fetchRepo(repoID)
          if (res.ok) {
            return makeRepositoryReference(res.payload)
          }
          return null
        }
        const has = await hasCustomInstructionsAsync(currentReferences, fetchRepoForInstructions)
        if (!cancelled) {
          setCustomInstructionsExist(has)
        }
      } else {
        const has = hasCustomInstructions(currentReferences)
        if (!cancelled) {
          setCustomInstructionsExist(has)
        }
      }
    }
    void fetchHasCustomInstructions()

    return () => {
      cancelled = true
    }
  }, [currentReferences, manager.service])

  const handleClickViewAll = useCallback(() => {
    // We might not have tried to load any threads but the latest one yet.
    void manager.fetchThreads()

    manager.viewAllThreads()
    sendEvent('copilot.view-conversations-clicked')
  }, [manager])

  const handleSelectDelete = () => {
    props.handleDelete()
    setOpen(false)
  }

  const userIsStaff = useMemo(() => {
    return isStaff()
  }, [])

  const deleteDisabled = !props.thread

  return (
    <>
      <ActionMenu open={open} onOpenChange={() => setOpen(prev => !prev)} anchorRef={props.anchorRef}>
        <ActionMenu.Anchor>
          <IconButton
            icon={KebabHorizontalIcon}
            variant="invisible"
            aria-label="Conversation options"
            tooltipDirection="n"
            className={styles.actionButton}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            {customInstructionsExist && (
              <ActionList.Item
                onSelect={() => {
                  manager.toggleRepoCustomInstructions(!repoCustomInstructionsEnabled)
                }}
              >
                <ActionList.LeadingVisual>
                  <NoteIcon />
                </ActionList.LeadingVisual>
                {repoCustomInstructionsEnabled ? (
                  <span>Disable custom instructions</span>
                ) : (
                  <span>Enable custom instructions</span>
                )}
              </ActionList.Item>
            )}
            <ActionList.Item
              variant={deleteDisabled ? 'default' : 'danger'}
              onSelect={handleSelectDelete}
              disabled={deleteDisabled}
              aria-describedby="delete-conversation-description"
            >
              <ActionList.LeadingVisual>
                <TrashIcon />
              </ActionList.LeadingVisual>
              Delete conversation
              <p id="delete-conversation-description" className="sr-only">
                This is a destructive action that cannot be undone
              </p>
            </ActionList.Item>
            <ActionList.Divider />
            <ActionList.Item onSelect={() => props.setShowPersonalInstructionsDialog(true)}>
              <ActionList.LeadingVisual>
                <ToolsIcon />
              </ActionList.LeadingVisual>
              Personal instructions
            </ActionList.Item>
            <ActionList.Item onSelect={handleClickViewAll}>
              <ActionList.LeadingVisual>
                <HistoryIcon />
              </ActionList.LeadingVisual>
              View all conversations
            </ActionList.Item>
            {optedInToUserFeedback && (
              <ActionList.Item
                onSelect={() => {
                  feedbackRef.current?.openDialog()
                  sendEvent('dotcom_chat.activate', {target: 'META_CONTEXT_MENU_GIVE_FEEDBACK', mode: 'assistive'})
                }}
              >
                <ActionList.LeadingVisual>
                  <CommentDiscussionIcon />
                </ActionList.LeadingVisual>
                Give feedback
              </ActionList.Item>
            )}
            {userIsStaff && (
              <ActionList.Item onSelect={() => props.setShowStaffDialog(DialogType.Experiments)}>
                <ActionList.LeadingVisual>
                  <BeakerIcon />
                </ActionList.LeadingVisual>
                Configure experiments
                <ActionList.TrailingVisual>
                  <Label variant="attention">Staff</Label>
                </ActionList.TrailingVisual>
              </ActionList.Item>
            )}
            {userIsStaff && showPromptDialog && (
              <ActionList.Item onSelect={() => props.setShowStaffDialog(DialogType.Prompt)}>
                <ActionList.LeadingVisual>
                  <BeakerIcon />
                </ActionList.LeadingVisual>
                Adjust prompts
                <ActionList.TrailingVisual>
                  <Label variant="attention">Staff</Label>
                </ActionList.TrailingVisual>
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <ConversationFeedbackDialog ref={feedbackRef} mode="assistive" />
    </>
  )
}
